import React, { useState, useEffect, useCallback } from 'react';
import { useConfirm } from '../../components/ConfirmModal';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

// ── Constants ────────────────────────────────────────────────────────────────

const AUDIENCES = [
  { value: 'all',    label: '👥 All Users',     desc: 'Every registered user' },
  { value: 'paid',   label: '💳 All Paid',       desc: 'Basic + Family (active)' },
  { value: 'basic',  label: '⭐ Basic Plan',      desc: 'Basic plan (active)' },
  { value: 'family', label: '👑 Family Plan',     desc: 'Family plan (active)' },
  { value: 'free',   label: '🆓 Free Plan',       desc: 'Free / unsubscribed users' },
];

const TEMPLATES = [
  {
    label: 'New Feature Announcement',
    subject: '✨ New feature just launched on Dreamy Bedtime!',
    html: `<p>Hi {{name}},</p>
<p>We're excited to announce a brand-new feature on Dreamy Bedtime!</p>
<p><strong>[Describe the feature here]</strong></p>
<p>Log in to try it today: <a href="https://dreamybedtime.com/dashboard">Open Dreamy Bedtime →</a></p>
<p>Sweet dreams,<br/>The Dreamy Bedtime Team</p>`,
  },
  {
    label: 'Upgrade Promotion',
    subject: '🌟 Unlock unlimited stories — upgrade today',
    html: `<p>Hi {{name}},</p>
<p>You're making great bedtime memories. Ready to do even more?</p>
<p>Upgrade to our <strong>Family Plan</strong> and get:</p>
<ul>
  <li>Unlimited children profiles</li>
  <li>Voice cloning — stories in your own voice</li>
  <li>40 narrations every month</li>
</ul>
<p><a href="https://dreamybedtime.com/pricing">See Pricing →</a></p>
<p>Sweet dreams,<br/>The Dreamy Bedtime Team</p>`,
  },
  {
    label: 'Maintenance Notice',
    subject: '🔧 Scheduled maintenance — Dreamy Bedtime',
    html: `<p>Hi {{name}},</p>
<p>We'll be performing scheduled maintenance on <strong>[DATE] from [START_TIME] to [END_TIME] UTC</strong>.</p>
<p>During this window, the app may be temporarily unavailable. We apologize for the inconvenience.</p>
<p>Thank you for your patience,<br/>The Dreamy Bedtime Team</p>`,
  },
  {
    label: 'Win-back (Inactive Users)',
    subject: '🌙 We miss you at Dreamy Bedtime',
    html: `<p>Hi {{name}},</p>
<p>It's been a while! Your children's next favourite bedtime story is waiting to be created.</p>
<p>Come back and generate a new story tonight — it only takes a minute.</p>
<p><a href="https://dreamybedtime.com/dashboard/generate">Create a story now →</a></p>
<p>Sweet dreams,<br/>The Dreamy Bedtime Team</p>`,
  },
];

const EVENT_LABELS = {
  delivered:   { label: 'Delivered',    color: '#15803d', bg: '#dcfce7', icon: '📬' },
  open:        { label: 'Opened',       color: '#1d4ed8', bg: '#dbeafe', icon: '👁️' },
  click:       { label: 'Clicked',      color: '#7c3aed', bg: '#ede9fe', icon: '🖱️' },
  bounce:      { label: 'Bounced',      color: '#b45309', bg: '#fef3c7', icon: '↩️' },
  spamreport:  { label: 'Spam Report',  color: '#be123c', bg: '#ffe4e6', icon: '🚫' },
  unsubscribe: { label: 'Unsubscribed', color: '#9a3412', bg: '#ffedd5', icon: '🔕' },
  dropped:     { label: 'Dropped',      color: 'var(--text-muted2)', bg: 'var(--header-bg)', icon: '🗑️' },
  deferred:    { label: 'Deferred',     color: '#0369a1', bg: '#e0f2fe', icon: '⏳' },
};

const AUDIENCE_LABELS = {
  all: '👥 All Users', paid: '💳 All Paid', basic: '⭐ Basic',
  family: '👑 Family', free: '🆓 Free',
};

const STATUS_STYLES = {
  sent:    { color: '#15803d', bg: '#dcfce7', label: 'Sent' },
  sending: { color: '#1d4ed8', bg: '#dbeafe', label: 'Sending…' },
  failed:  { color: '#be123c', bg: '#ffe4e6', label: 'Failed' },
};

const pct = (num, denom) => denom ? Math.round((num / denom) * 100) : 0;
const fmt = (n) => (n || 0).toLocaleString();

// ── Stat chip ────────────────────────────────────────────────────────────────
const StatChip = ({ icon, label, value, color, bg, rate }) => (
  <div style={{ background: bg, borderRadius: '10px', padding: '10px 14px', minWidth: '90px' }}>
    <div style={{ fontSize: '18px', marginBottom: '2px' }}>{icon}</div>
    <div style={{ fontSize: '20px', fontWeight: 700, color }}>{fmt(value)}</div>
    {rate !== undefined && <div style={{ fontSize: '11px', color, opacity: 0.8 }}>{rate}%</div>}
    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginTop: '2px' }}>{label}</div>
  </div>
);

// ── Compose Tab ──────────────────────────────────────────────────────────────
const ComposeTab = ({ onSent }) => {
  const confirm = useConfirm();
  const [audience,      setAudience]      = useState('all');
  const [subject,       setSubject]       = useState('');
  const [htmlBody,      setHtmlBody]      = useState('');
  const [preview,       setPreview]       = useState(false);
  const [audienceCount, setAudienceCount] = useState(null);
  const [loadingCount,  setLoadingCount]  = useState(false);
  const [sending,       setSending]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [template,      setTemplate]      = useState('');

  useEffect(() => {
    setAudienceCount(null);
    setLoadingCount(true);
    API.get('/admin/broadcast/count', { params: { audience } })
      .then(r => setAudienceCount(r.data.count))
      .catch(() => setAudienceCount(null))
      .finally(() => setLoadingCount(false));
  }, [audience]);

  const applyTemplate = (idx) => {
    if (idx === '') return;
    const t = TEMPLATES[parseInt(idx)];
    setSubject(t.subject);
    setHtmlBody(t.html);
    setTemplate(idx);
  };

  const unfilledPlaceholders = [...(htmlBody.matchAll(/\[[A-Z][^\]]*\]/g) || [])]
    .map(m => m[0]).filter((v, i, a) => a.indexOf(v) === i);

  const handleSend = async () => {
    if (!subject.trim() || !htmlBody.trim()) {
      setResult({ success: false, message: 'Subject and body are required.' });
      return;
    }
    const ok = await confirm({ title: 'Send broadcast email?', message: `This will send to ${audienceCount?.toLocaleString() || '?'} recipients immediately. This cannot be undone.`, confirmLabel: 'Send Now', danger: true });
    if (!ok) return;
    setSending(true);
    setResult(null);
    try {
      const r = await API.post('/admin/broadcast', { subject, htmlBody, audience });
      setResult({ success: true, message: r.data.message });
      setSubject('');
      setHtmlBody('');
      setTemplate('');
      onSent?.();
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.message || 'Failed to send email.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="row g-4">
      {/* Left: Compose */}
      <div className="col-lg-7">
        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Compose Broadcast Email</h6>
            <p className="text-muted small mb-4">
              Use <code>{'{{name}}'}</code> to personalise with each recipient's first name.
            </p>

            {/* Template */}
            <div className="mb-3">
              <label className="form-label small fw-semibold" style={{ color: 'var(--text-main)' }}>Quick Template</label>
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={template} onChange={e => applyTemplate(e.target.value)}>
                <option value="">— Start from a template —</option>
                {TEMPLATES.map((t, i) => <option key={i} value={i}>{t.label}</option>)}
              </select>
            </div>

            {/* Subject */}
            <div className="mb-3">
              <label className="form-label small fw-semibold" style={{ color: 'var(--text-main)' }}>Subject Line</label>
              <input
                className="form-control" style={{ borderRadius: '10px' }}
                placeholder="e.g. ✨ New feature just launched!"
                value={subject} onChange={e => setSubject(e.target.value)}
              />
            </div>

            {/* Body */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label small fw-semibold mb-0" style={{ color: 'var(--text-main)' }}>Email Body (HTML)</label>
                <button type="button"
                  className={`btn btn-xs rounded-pill px-3 ${preview ? 'btn-dream' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '12px' }}
                  onClick={() => setPreview(p => !p)}>
                  {preview ? '✏️ Edit' : '👁 Preview'}
                </button>
              </div>
              {preview ? (
                <div className="border rounded-3 p-4"
                  style={{ minHeight: '320px', background: 'var(--card-bg)', fontSize: '14px', lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: htmlBody.replace(/\{\{name\}\}/g, 'Emma') }}
                />
              ) : (
                <textarea
                  className="form-control font-monospace"
                  style={{ borderRadius: '10px', minHeight: '320px', fontSize: '13px', resize: 'vertical' }}
                  placeholder="<p>Hi {{name}},</p><p>...</p>"
                  value={htmlBody} onChange={e => setHtmlBody(e.target.value)}
                />
              )}
              <div className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                HTML supported. <code>{'{{name}}'}</code> → recipient's first name. Click tracking &amp; open tracking are enabled automatically.
              </div>
            </div>

            {/* Unfilled placeholder warning */}
            {unfilledPlaceholders.length > 0 && (
              <div className="alert alert-warning py-2 small mb-3 d-flex align-items-start gap-2" style={{ borderRadius: '10px' }}>
                <span>⚠️</span>
                <span>
                  Unfilled placeholders:{' '}
                  {unfilledPlaceholders.map((p, i) => <code key={i} className="me-1">{p}</code>)}
                  — replace these before sending.
                </span>
              </div>
            )}

            {result && (
              <div className={`alert ${result.success ? 'alert-success' : 'alert-danger'} py-2 small mb-3`} style={{ borderRadius: '10px' }}>
                {result.message}
              </div>
            )}

            <button
              className="btn btn-dream rounded-pill px-5"
              disabled={sending || !subject.trim() || !htmlBody.trim()}
              onClick={handleSend}
              style={{ fontSize: '14px' }}>
              {sending
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</>
                : <><i className="bi bi-send me-2"></i>Send to {loadingCount ? '…' : (audienceCount?.toLocaleString() ?? '?')} Users</>}
            </button>
          </div>
        </div>
      </div>

      {/* Right: Audience + tips */}
      <div className="col-lg-5">
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Target Audience</h6>
            <div className="d-flex flex-column gap-2">
              {AUDIENCES.map(a => (
                <label key={a.value}
                  className="d-flex align-items-center gap-3 p-3 rounded-3"
                  style={{
                    cursor: 'pointer',
                    background: audience === a.value ? 'var(--purple-section)' : 'var(--header-bg)',
                    border: `2px solid ${audience === a.value ? '#6f42c1' : 'transparent'}`,
                    transition: 'all 0.15s',
                  }}>
                  <input type="radio" name="audience" value={a.value}
                    checked={audience === a.value}
                    onChange={() => setAudience(a.value)}
                    style={{ accentColor: '#6f42c1' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{a.label}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{a.desc}</div>
                  </div>
                  {audience === a.value && (
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {loadingCount
                        ? <span className="spinner-border spinner-border-sm" style={{ color: '#6f42c1' }}></span>
                        : <span style={{ fontSize: '18px', fontWeight: 700, color: '#6f42c1' }}>{audienceCount?.toLocaleString() ?? '—'}</span>}
                      <div style={{ fontSize: '10px', color: 'var(--text-soft)' }}>recipients</div>
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Tips</h6>
            <div className="d-flex flex-column gap-2" style={{ fontSize: '13px', color: 'var(--text-body)' }}>
              {[
                ['✅', <><code>{'{{name}}'}</code> → personalised first name</>],
                ['✅', 'Open + click tracking enabled automatically'],
                ['✅', 'Preview before sending to catch layout issues'],
                ['⚠️', 'Sends are immediate and cannot be recalled'],
                ['⚠️', 'Rate limited to 2 sends per hour'],
              ].map(([icon, text], i) => (
                <div key={i} className="d-flex gap-2"><span>{icon}</span><span>{text}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Campaign Detail Modal ────────────────────────────────────────────────────
const CampaignDetailModal = ({ campaign, onClose }) => {
  const [events,      setEvents]      = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [page,        setPage]        = useState(1);
  const [pages,       setPages]       = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('');

  const load = useCallback((p = 1, evt = filter) => {
    setLoading(true);
    const params = { page: p, limit: 50 };
    if (evt) params.event = evt;
    API.get(`/admin/broadcast/${campaign._id}`, { params })
      .then(r => {
        setEvents(r.data.events);
        setTotalEvents(r.data.totalEvents);
        setPages(r.data.pages);
        setPage(p);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaign._id, filter]);

  useEffect(() => { load(1, ''); }, [campaign._id]); // eslint-disable-line

  const handleFilter = (evt) => {
    setFilter(evt);
    load(1, evt);
  };

  const s   = campaign.stats || {};
  const sent = campaign.sentCount || 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '32px 16px', overflowY: 'auto',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--card-bg)', borderRadius: '20px', width: '100%', maxWidth: '860px', padding: '32px' }}>
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div>
            <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>{campaign.subject}</h5>
            <div className="d-flex gap-2 flex-wrap" style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>
              <span>{AUDIENCE_LABELS[campaign.audience] || campaign.audience}</span>
              <span>·</span>
              <span>{new Date(campaign.createdAt).toLocaleString()}</span>
              <span>·</span>
              <span>{fmt(sent)} sent</span>
              {(() => {
                const st = STATUS_STYLES[campaign.status] || STATUS_STYLES.sent;
                return (
                  <span className="badge" style={{ background: st.bg, color: st.color, fontWeight: 600 }}>
                    {st.label}
                  </span>
                );
              })()}
            </div>
          </div>
          <button className="btn-close" onClick={onClose}></button>
        </div>

        {/* Stats */}
        <div className="d-flex flex-wrap gap-2 mb-4">
          {[
            { key: 'delivered',   stat: s.delivered,    rate: pct(s.delivered,   sent) },
            { key: 'open',        stat: s.opened,       rate: pct(s.opened,      s.delivered) },
            { key: 'click',       stat: s.clicked,      rate: pct(s.clicked,     s.delivered) },
            { key: 'bounce',      stat: s.bounced,      rate: pct(s.bounced,     sent) },
            { key: 'spamreport',  stat: s.spamReports,  rate: pct(s.spamReports, sent) },
            { key: 'unsubscribe', stat: s.unsubscribed, rate: pct(s.unsubscribed,sent) },
            { key: 'dropped',     stat: s.dropped,      rate: pct(s.dropped,     sent) },
            { key: 'deferred',    stat: s.deferred,     rate: pct(s.deferred,    sent) },
          ].map(({ key, stat, rate }) => {
            const meta = EVENT_LABELS[key];
            return (
              <StatChip key={key}
                icon={meta.icon} label={meta.label} value={stat || 0}
                color={meta.color} bg={meta.bg} rate={rate}
              />
            );
          })}
        </div>

        {/* Event filter */}
        <div className="d-flex gap-2 flex-wrap mb-3">
          <button
            className={`btn btn-sm rounded-pill ${!filter ? 'btn-dream' : 'btn-outline-secondary'}`}
            onClick={() => handleFilter('')}>All events</button>
          {Object.entries(EVENT_LABELS).map(([key, meta]) => (
            <button key={key}
              className={`btn btn-sm rounded-pill ${filter === key ? 'btn-dream' : 'btn-outline-secondary'}`}
              onClick={() => handleFilter(key)}>
              {meta.icon} {meta.label}
            </button>
          ))}
        </div>

        {/* Events table */}
        {loading ? (
          <div className="text-center py-4">
            <span className="spinner-border" style={{ color: '#6f42c1' }}></span>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-4 text-muted small">No events yet — webhook data appears here as SendGrid delivers them.</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-sm" style={{ fontSize: '13px' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted2)' }}>
                    <th>Email</th>
                    <th>Event</th>
                    <th>Time</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e, i) => {
                    const meta = EVENT_LABELS[e.event] || { label: e.event, color: 'var(--text-body)', bg: 'var(--header-bg)', icon: '•' };
                    return (
                      <tr key={i}>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{e.email}</td>
                        <td>
                          <span className="badge rounded-pill" style={{ background: meta.bg, color: meta.color, fontWeight: 600 }}>
                            {meta.icon} {meta.label}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-muted2)', whiteSpace: 'nowrap' }}>
                          {new Date(e.timestamp).toLocaleString()}
                        </td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.url && <a href={e.url} target="_blank" rel="noreferrer" style={{ color: '#6f42c1', fontSize: '12px' }}>{e.url}</a>}
                          {e.bounceType && <span style={{ fontSize: '12px', color: '#b45309' }}>{e.bounceType} bounce{e.reason ? ` — ${e.reason}` : ''}</span>}
                          {e.reason && !e.bounceType && <span style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{e.reason}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{fmt(totalEvents)} total events</span>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-secondary rounded-pill"
                    disabled={page <= 1} onClick={() => load(page - 1)}>←</button>
                  <span style={{ fontSize: '13px', lineHeight: '32px' }}>Page {page} / {pages}</span>
                  <button className="btn btn-sm btn-outline-secondary rounded-pill"
                    disabled={page >= pages} onClick={() => load(page + 1)}>→</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── History Tab ──────────────────────────────────────────────────────────────
const HistoryTab = ({ refreshKey }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [pages,     setPages]     = useState(1);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);

  const load = useCallback((p = 1) => {
    setLoading(true);
    API.get('/admin/broadcast', { params: { page: p, limit: 20 } })
      .then(r => {
        setCampaigns(r.data.campaigns);
        setTotal(r.data.total);
        setPages(r.data.pages);
        setPage(p);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1); }, [load, refreshKey]);

  if (loading) return (
    <div className="text-center py-5">
      <span className="spinner-border" style={{ color: '#6f42c1', width: '2.5rem', height: '2.5rem' }}></span>
    </div>
  );

  if (campaigns.length === 0) return (
    <div className="text-center py-5 text-muted">
      <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
      <p>No broadcast campaigns yet. Send your first one from the Compose tab.</p>
    </div>
  );

  return (
    <>
      {selected && (
        <CampaignDetailModal campaign={selected} onClose={() => setSelected(null)} />
      )}

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <span style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{fmt(total)} campaign{total !== 1 ? 's' : ''}</span>
      </div>

      <div className="d-flex flex-column gap-3">
        {campaigns.map(c => {
          const s  = c.stats || {};
          const st = STATUS_STYLES[c.status] || STATUS_STYLES.sent;
          return (
            <div key={c._id}
              className="card border-0 shadow-sm"
              style={{ borderRadius: '14px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onClick={() => setSelected(c)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(111,66,193,0.15)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                  <div style={{ flex: 1 }}>
                    <div className="fw-semibold mb-1" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{c.subject}</div>
                    <div className="d-flex gap-2 flex-wrap" style={{ fontSize: '12px', color: 'var(--text-soft)' }}>
                      <span>{AUDIENCE_LABELS[c.audience] || c.audience}</span>
                      <span>·</span>
                      <span>{new Date(c.createdAt).toLocaleString()}</span>
                      <span>·</span>
                      <span>{fmt(c.sentCount)} recipients</span>
                    </div>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="badge rounded-pill" style={{ background: st.bg, color: st.color, fontWeight: 600, fontSize: '12px' }}>
                      {st.label}
                    </span>
                    <i className="bi bi-chevron-right" style={{ color: 'var(--text-soft)' }}></i>
                  </div>
                </div>

                {/* Mini stats row */}
                <div className="d-flex flex-wrap gap-3" style={{ fontSize: '12px' }}>
                  {[
                    { icon: '📬', label: 'Delivered', val: s.delivered,    rate: pct(s.delivered,   c.sentCount) },
                    { icon: '👁️', label: 'Opened',    val: s.opened,       rate: pct(s.opened,      s.delivered) },
                    { icon: '🖱️', label: 'Clicked',   val: s.clicked,      rate: pct(s.clicked,     s.delivered) },
                    { icon: '↩️', label: 'Bounced',   val: s.bounced,      rate: pct(s.bounced,     c.sentCount) },
                    { icon: '🚫', label: 'Spam',       val: s.spamReports,  rate: pct(s.spamReports, c.sentCount) },
                  ].map(({ icon, label, val, rate }) => (
                    <div key={label} style={{ textAlign: 'center', minWidth: '60px' }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-main)' }}>{fmt(val)}</div>
                      <div style={{ color: 'var(--text-soft)' }}>{rate}% {icon} {label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {pages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-4">
          <button className="btn btn-sm btn-outline-secondary rounded-pill"
            disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ fontSize: '13px', lineHeight: '32px' }}>Page {page} / {pages}</span>
          <button className="btn btn-sm btn-outline-secondary rounded-pill"
            disabled={page >= pages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}
    </>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────
const AdminBroadcast = () => {
  const [tab,        setTab]        = useState('compose');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSent = () => {
    setRefreshKey(k => k + 1);
    setTab('history');
  };

  return (
    <AdminLayout title="Broadcast Email">
      {/* Tabs */}
      <div className="d-flex gap-2 mb-4">
        {[
          { key: 'compose', icon: 'bi-pencil-square', label: 'Compose' },
          { key: 'history', icon: 'bi-clock-history',  label: 'History' },
        ].map(t => (
          <button key={t.key}
            className={`btn rounded-pill px-4 fw-semibold ${tab === t.key ? 'btn-dream' : 'btn-outline-secondary'}`}
            style={{ fontSize: '14px' }}
            onClick={() => setTab(t.key)}>
            <i className={`bi ${t.icon} me-2`}></i>{t.label}
          </button>
        ))}
      </div>

      {tab === 'compose' && <ComposeTab onSent={handleSent} />}
      {tab === 'history' && <HistoryTab refreshKey={refreshKey} />}
    </AdminLayout>
  );
};

export default AdminBroadcast;
